import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, 
  Lock, 
  CheckCircle,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { useAuth, API } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const token = searchParams.get('token');
  
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (token) {
      fetchInvitation();
    } else {
      setError('No se proporcionó un token de invitación');
      setLoading(false);
    }
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const response = await axios.get(`${API}/auth/invitation/${token}`);
      setInvitation(response.data);
    } catch (error) {
      console.error('Error fetching invitation:', error);
      const message = error.response?.data?.detail || 'Invitación inválida o expirada';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/auth/accept-invitation`, null, {
        params: { token, password }
      });
      
      // Login with the returned token
      const userData = response.data;
      localStorage.setItem('token', userData.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${userData.access_token}`;
      
      setSuccess(true);
      toast.success('¡Cuenta activada exitosamente!');
      
      // Redirect after short delay
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      const message = error.response?.data?.detail || 'Error al aceptar invitación';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invitación Inválida</h2>
            <p className="text-slate-500 mb-6">{error}</p>
            <Link to="/login">
              <Button className="bg-orange-500 hover:bg-orange-600">
                Ir al Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">¡Bienvenido a LivCom!</h2>
            <p className="text-slate-500 mb-4">
              Tu cuenta ha sido activada exitosamente. Serás redirigido al dashboard...
            </p>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="accept-invitation-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto bg-orange-100 p-3 rounded-2xl w-fit mb-4">
            <Building2 className="h-8 w-8 text-orange-500" />
          </div>
          <CardTitle className="text-2xl">Aceptar Invitación</CardTitle>
          <CardDescription>
            Has sido invitado a administrar
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Invitation Details */}
          <div className="bg-orange-50 p-4 rounded-xl mb-6 border border-orange-100">
            <p className="text-sm text-orange-600 font-medium mb-1">Conjunto</p>
            <p className="text-lg font-bold text-slate-900">{invitation?.building_name}</p>
            <p className="text-sm text-slate-500 mt-2">
              Invitación para: <strong>{invitation?.name}</strong>
            </p>
            <p className="text-xs text-slate-400 mt-1">{invitation?.email}</p>
          </div>

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Crear Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  data-testid="password-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Repite tu contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  data-testid="confirm-password-input"
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="accept-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Activando cuenta...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Aceptar y Comenzar
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-6">
            Al aceptar, confirmas los términos y condiciones de LivCom
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
